import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatPercentage } from '@/lib/utils/format';
import type { MarginsData, MarginEntry } from '../types/analytics.types';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

interface MarginsTabProps {
  data: MarginsData | null;
  isLoading: boolean;
}

function MarginChart({
  title,
  entries,
  color,
}: {
  title: string;
  entries: MarginEntry[];
  color: string;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">
            Sin datos
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={entries} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                className="fill-muted-foreground"
                tickFormatter={(v) => `${v.toFixed(0)}%`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                className="fill-muted-foreground"
                width={100}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={
                  ((value: any, name: string) => {
                    if (name === 'avgMargin')
                      return [formatPercentage(Number(value ?? 0)), 'Margen'];
                    return [value ?? 0, name];
                  }) as any
                }
                labelFormatter={(label) => String(label)}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const entry = payload[0].payload as MarginEntry;
                  return (
                    <div style={tooltipStyle} className="p-3 shadow-lg">
                      <p className="font-medium text-sm">{label}</p>
                      <p className="text-sm">Margen: {formatPercentage(entry.avgMargin)}</p>
                      <p className="text-sm">Ingresos: {formatCurrency(entry.totalRevenue)}</p>
                      <p className="text-sm text-muted-foreground">
                        {entry.quoteCount} cotizaciones
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="avgMargin" fill={color} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

export function MarginsTab({ data, isLoading }: MarginsTabProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-20" />
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-[280px] w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const overallKpis = [
    {
      label: 'Margen Promedio',
      value: formatPercentage(data.overall.avgMargin),
      color: 'text-blue-600',
    },
    {
      label: 'Margen Mínimo',
      value: formatPercentage(data.overall.lowestMargin),
      color: 'text-red-600',
    },
    {
      label: 'Margen Máximo',
      value: formatPercentage(data.overall.highestMargin),
      color: 'text-green-600',
    },
    {
      label: 'Ingresos Totales',
      value: formatCurrency(data.overall.totalRevenue),
      color: 'text-violet-600',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Overall KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {overallKpis.map((kpi) => (
          <Card key={kpi.label} className="p-4">
            <p className="text-xs text-muted-foreground">{kpi.label}</p>
            <p className={`text-2xl font-bold mt-1 ${kpi.color}`}>{kpi.value}</p>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <MarginChart title="Margen por Línea de Negocio" entries={data.byLob} color="#6366f1" />
        <MarginChart title="Margen por Vendedor" entries={data.bySeller} color="#8b5cf6" />
        <MarginChart title="Margen por Cliente" entries={data.byCustomer} color="#a78bfa" />
      </div>
    </div>
  );
}
